import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import { contributionConstants } from "./types";
import {
  addContribution,
  addPaycheckContribution,
  editPaycheckContribution,
  submitOnboardingPayrollContribution,
  getRecommendedContributionAmounts,
  getContributionAbilityRequest,
  addOnboardingRecurringContribution,
  getRecurringContributions,
  addRecurringContribution,
  getContributionAbilityAndEstimatesRequest,
  submitOnboardingPaycheckSplitContribution,
  addPaycheckSplitContribution,
  editPaycheckSplitContribution,
  deletePaycheckSplitContribution,
} from "services/contributionService";
import { userConstants, bankConstants } from "actions/types";
import ActionCreator from "utils/actionHandler";
import { getTransactions } from "./transactionActions";
import { getIraAccountBalance } from "./accountActions";
import type { AppDispatch } from "store/configureStore";

export const getContributionAbility = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getContributionAbilityRequest>>
    >(contributionConstants.GET_CONTRIBUTION_ABILITY);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getContributionAbilityRequest(client)
    );
  };
};

export const addContributionSource = (
  client: ApolloClient<object>,
  {
    iraAccountId,
    amount,
    bankId,
    applyToPriorYear,
  }: {
    iraAccountId: string;
    amount: number;
    bankId: string;
    applyToPriorYear?: boolean;
  }
) => {
  return async (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof addContribution>>
    >(contributionConstants.CONTRIBUTION);
    // Any new transaction/contribution ability endpoints should be called after contribution is made
    await actionCreator.dispatchFullLifecycle(
      dispatch,
      addContribution({
        client,
        iraAccountId,
        amount,
        bankId,
        applyToPriorYear,
      })
    );
    // fire-and-forget refreshes (pre-existing behavior); the balance query
    // declares no variables, so iraAccountId only satisfies the signature
    void dispatch(getIraAccountBalance(client, iraAccountId));
    void dispatch(getTransactions(client, iraAccountId));
    return getContributionAbility(client)(dispatch);
  };
};

export const addPaycheckContributionSource = (
  client: ApolloClient<object>,
  {
    amount,
    groupId,
    isDailyCustom,
    accountId,
  }: {
    amount: number;
    groupId: string;
    isDailyCustom: boolean;
    accountId: string;
  }
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof addPaycheckContribution>>
    >(contributionConstants.PAYCHECK_CONTRIBUTION);
    dispatch(actionCreator.request());
    return addPaycheckContribution({
      client,
      amount,
      groupId,
      isDailyCustom,
      accountId,
    }).then(
      (data) => {
        if (data) {
          dispatch(actionCreator.success(data));
          dispatch({
            type: userConstants.USER_EMPLOYER_LINK_REQUESTS_SUCCESS,
            data,
          });
        }
      },
      (error: Error) => {
        console.log("addPaycheckContribution Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const editPaycheckContributionSource = (
  client: ApolloClient<object>,
  params: Parameters<typeof editPaycheckContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof editPaycheckContribution>>
    >(contributionConstants.EDIT_PAYCHECK_CONTRIBUTION);
    dispatch(actionCreator.request());

    return editPaycheckContribution(client, params).then(
      (data) => {
        if (data) {
          dispatch(actionCreator.success(data));
          dispatch({ type: userConstants.USER_EMPLOYER_LINK_SUCCESS, data });
        }
      },
      (error: Error) => {
        console.log("editPaycheckContributionSource Error: " + String(error));
        dispatch(actionCreator.failure(error));
      }
    );
  };
};

export const makeOnboardingPayrollContribution = (
  client: ApolloClient<object>,
  params: Parameters<typeof submitOnboardingPayrollContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitOnboardingPayrollContribution>>
    >(contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitOnboardingPayrollContribution(client, params)
    );
  };
};

export const getContributionRecommendations = (
  client: ApolloClient<object>,
  params: Parameters<typeof getRecommendedContributionAmounts>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getRecommendedContributionAmounts>>
    >(contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getRecommendedContributionAmounts(client, params)
    );
  };
};

export const getContributionAbilityAndEstimates = (
  client: ApolloClient<object>
) => {
  return (dispatch: Dispatch) => {
    const estimateAction = new ActionCreator<
      Awaited<
        ReturnType<typeof getContributionAbilityAndEstimatesRequest>
      >["estimates"]
    >(contributionConstants.GET_CONTRIBUTION_ESTIMATES);
    const contributionAbilityAction = new ActionCreator<
      Awaited<
        ReturnType<typeof getContributionAbilityAndEstimatesRequest>
      >["contributionAbility"]
    >(contributionConstants.GET_CONTRIBUTION_ABILITY);

    dispatch(estimateAction.request());
    dispatch(contributionAbilityAction.request());

    return getContributionAbilityAndEstimatesRequest(client)
      .then((data) => {
        if (data) {
          dispatch(estimateAction.success(data.estimates));
          dispatch(contributionAbilityAction.success(data.contributionAbility));
        }
      })
      .catch((err: Error) => {
        console.log("getContributionAbilityAndEstimates Error: " + String(err));
        dispatch(estimateAction.failure(err));
        dispatch(contributionAbilityAction.failure(err));
      });
  };
};

export const makeOnboardingRecurringContribution = (
  client: ApolloClient<object>,
  params: Parameters<typeof addOnboardingRecurringContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof addOnboardingRecurringContribution>>
    >(contributionConstants.ONBOARDING_RECURRING_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      addOnboardingRecurringContribution(client, params)
    );
  };
};

export const createRecurringContribution = (
  client: ApolloClient<object>,
  params: Parameters<typeof addRecurringContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof addRecurringContribution>>
    >(contributionConstants.ADD_RECURRING_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      addRecurringContribution(client, params),
      {},
      (data) => {
        return dispatch({
          type: bankConstants.SET_RECURRING_SCHEDULE,
          data: { recurringContribution: data, bankId: params.bankId },
        });
      }
    );
  };
};

// ---------------------------------------------------------------------------
// Paychex Flex (Perks) PAYCHECK_SPLIT actions. Mirror the employer-group
// paycheck actions above but keyed off a worker record. Components re-fetch
// worker records after a successful CRUD mutation (see getPaychexWorkerRecords).
// ---------------------------------------------------------------------------

export const makeOnboardingPaycheckSplitContribution = (
  client: ApolloClient<object>,
  params: Parameters<typeof submitOnboardingPaycheckSplitContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitOnboardingPaycheckSplitContribution>>
    >(contributionConstants.ONBOARDING_PAYCHECK_SPLIT_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitOnboardingPaycheckSplitContribution(client, params)
    );
  };
};

export const addPaycheckSplitContributionSource = (
  client: ApolloClient<object>,
  params: Parameters<typeof addPaycheckSplitContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof addPaycheckSplitContribution>>
    >(contributionConstants.PAYCHECK_SPLIT_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      addPaycheckSplitContribution(client, params)
    );
  };
};

export const editPaycheckSplitContributionSource = (
  client: ApolloClient<object>,
  params: Parameters<typeof editPaycheckSplitContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof editPaycheckSplitContribution>>
    >(contributionConstants.EDIT_PAYCHECK_SPLIT_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      editPaycheckSplitContribution(client, params)
    );
  };
};

export const deletePaycheckSplitContributionSource = (
  client: ApolloClient<object>,
  params: Parameters<typeof deletePaycheckSplitContribution>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof deletePaycheckSplitContribution>>
    >(contributionConstants.DELETE_PAYCHECK_SPLIT_CONTRIBUTION);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      deletePaycheckSplitContribution(client, params)
    );
  };
};

export const getUserRecurringContributions = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getRecurringContributions>>
    >(contributionConstants.GET_RECURRING_CONTRIBUTIONS);

    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getRecurringContributions(client)
    );
  };
};
